import { ApiError, type ServicePlanBilling, type ServicePlanBillingInterval } from '@mevaser/shared'
import {
  Button,
  Field,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

const toIntOrNull = (raw: string) =>
  raw.trim() === '' ? null : Math.max(0, Math.trunc(Number(raw)))

/** Billing details for a `plan` product (t1077). Renders nothing for other types. */
export function PlanBillingSection({
  productId,
  type,
}: {
  productId: string | null | undefined
  type: string
}) {
  if (type !== 'plan') return null
  if (!productId) {
    return (
      <p className="text-xs text-foreground-muted">
        Save the plan first, then set its billing details.
      </p>
    )
  }
  return <PlanBillingForm productId={productId} />
}

function PlanBillingForm({ productId }: { productId: string }) {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canWrite = can('catalog:write')
  const queryKey = ['products', productId, 'service-plan'] as const

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => api.get<ServicePlanBilling | null>(`/products/${productId}/service-plan`),
  })

  const [priceId, setPriceId] = useState('')
  const [interval, setInterval] = useState<ServicePlanBillingInterval>('month')
  const [dollars, setDollars] = useState('')
  const [minutes, setMinutes] = useState('')
  const [texts, setTexts] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isLoading) return
    setPriceId(data?.stripePriceId ?? '')
    setInterval(data?.billingInterval ?? 'month')
    setDollars(data ? (data.monthlyCents / 100).toFixed(2) : '')
    setMinutes(data?.includedMinutes?.toString() ?? '')
    setTexts(data?.includedTexts?.toString() ?? '')
  }, [data, isLoading])

  const save = useMutation({
    mutationFn: () =>
      api.put<ServicePlanBilling>(`/products/${productId}/service-plan`, {
        stripePriceId: priceId.trim() === '' ? null : priceId.trim(),
        billingInterval: interval,
        monthlyCents: Math.round(Number(dollars || 0) * 100),
        includedMinutes: toIntOrNull(minutes),
        includedTexts: toIntOrNull(texts),
      }),
    onSuccess: () => {
      setError(null)
      queryClient.invalidateQueries({ queryKey })
      toast.success('Plan billing saved')
    },
    onError: (e) => {
      const message = e instanceof ApiError ? e.message : 'Could not save the plan billing'
      setError(message)
      toast.error('Could not save the plan billing', e instanceof ApiError ? e.message : undefined)
    },
  })

  return (
    <fieldset className="space-y-3 rounded-lg border border-border p-3" disabled={!canWrite}>
      <legend className="px-1 text-sm font-medium">Plan billing</legend>
      <Field
        label="Stripe price ID"
        hint={`Optional — leave blank and a price is created in Stripe automatically on the first sale, from the amount and interval below. To use one you made in the Stripe dashboard instead, paste its ID (starts with price_); it must renew on the billing interval chosen below. The kiosk only offers a plan once a price is set.`}
      >
        {(props) => (
          <Input
            {...props}
            identifier
            value={priceId}
            onChange={(e) => setPriceId(e.target.value)}
            placeholder="price_…"
          />
        )}
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Billing interval">
          {(props) => (
            <Select
              value={interval}
              onValueChange={(v) => setInterval(v as ServicePlanBillingInterval)}
            >
              <SelectTrigger {...props}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="month">Monthly</SelectItem>
                <SelectItem value="thirty_days">Every 30 days</SelectItem>
                <SelectItem value="year">Yearly</SelectItem>
              </SelectContent>
            </Select>
          )}
        </Field>
        <Field label="Price per interval ($)">
          {(props) => (
            <Input
              {...props}
              type="number"
              min="0"
              step="0.01"
              value={dollars}
              onChange={(e) => setDollars(e.target.value)}
            />
          )}
        </Field>
        <Field label="Included minutes" hint="Blank = unlimited / not listed.">
          {(props) => (
            <Input
              {...props}
              type="number"
              min="0"
              step="1"
              value={minutes}
              onChange={(e) => setMinutes(e.target.value)}
            />
          )}
        </Field>
        <Field label="Included texts" hint="Blank = unlimited / not listed.">
          {(props) => (
            <Input
              {...props}
              type="number"
              min="0"
              step="1"
              value={texts}
              onChange={(e) => setTexts(e.target.value)}
            />
          )}
        </Field>
      </div>
      {error ? <p className="text-xs text-danger">{error}</p> : null}
      {canWrite ? (
        <Button
          type="button"
          variant="secondary"
          loading={save.isPending}
          onClick={() => save.mutate()}
        >
          Save plan billing
        </Button>
      ) : null}
    </fieldset>
  )
}
